<template>
  <video-player
    :id="id"
    :controls="{
      hide: true,
      muteButton: videoAudioControls,
      id: `${liquid.sectionId}-${id}`,
    }"
    :intersection-observer="autoPlay"
    :image="previewImage"
    :is-dark="isDark === 'true'"
    :ratio="ratio"
    :sources="sources"
    :poster="poster"
  />
</template>

<script>

/**
 * Vue: Media text split (media-text-split)
 * -----------------------------------------------------------------------------
 * Renders 2 columns containing an image or video in one, and text in the other.
 * - Limited interactivity section.
 *
 * @param {Boolean} isDark - Video Control Settings
 * @param {Object} previewMobile - Formatted image object.
 * @param {Object} previewDesktop - Formatted desktop image object.
 * @param {String} ratioMobile - Mobile video ratio.
 * @param {String} ratioDesktop - Desktop video ratio.
 * @param {Object} videoMobile - Formatted video object.
 * @param {Object} videoDesktop - Formatted desktop video object.
 * @param {Boolean} videoAutoPlay - Video autoplay setting.
 * @param {Boolean} videoAudioControls - Video audio controls setting.
 * @param {String} videoControlColor - Video control color settings
 * @param {String} poster - Video poster image.
 *
 */
import { mapState } from 'vuex'

import VideoPlayer from '~async/video-player/video-player'

export default {
  name: 'MediaTextSplit',

  components: {
    VideoPlayer,
  },

  props: {
    isDark: {
      type: String,
      default: 'lite',
    },
    previewMobile: {
      type: Object,
      default: () => ({}),
    },
    previewDesktop: {
      type: Object,
      default: () => ({}),
    },
    ratioMobile: {
      type: String,
      default: '',
    },
    ratioDesktop: {
      type: String,
      default: '16:9',
    },
    videoMobile: {
      type: Object,
      default: () => ({}),
    },
    videoDesktop: {
      type: Object,
      default: () => ({}),
    },
    videoAutoPlay: {
      type: Boolean,
      default: true,
    },
    videoAudioControls: {
      type: Boolean,
      default: true,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    poster: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute video ID.
     * @returns {Number}
     */
    id() {
      return this.isMobile ? this.videoMobile.id : this.videoDesktop.id
    },

    /**
     * Compute preview image.
     * @returns {Object}
     */
    previewImage() {
      return this.isMobile ? this.previewMobile : this.previewDesktop
    },

    /**
     * Compute ratio.
     * @returns {String}
     */
    ratio() {
      return this.isMobile ? this.ratioMobile : this.ratioDesktop
    },

    /**
     * Compute video sources.
     * @returns {Array}
     */
    sources() {
      return this.isMobile ? this.videoMobile.sources : this.videoDesktop.sources
    },

    /**
     * Compute autoplay settings.
     * @returns {Object}
     */
    autoPlay() {
      return { enable: this.videoAutoPlay }
    },
  },
}
</script>

<style lang="scss">
@import './media-text-split';
</style>
